import {toEither, Async, Loaded} from 'Core/models/Async';
import {filter, flatten, head, map, findFirst} from 'fp-ts/lib/Array';
import * as E from 'fp-ts/lib/Either';
import {constant, identity} from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {ReducerState} from 'rootReducer';
import {AgentNumberStatusId} from 'Shared/constants/AgentNumberStatus';
import {TerminatedAgentScopes} from 'Shared/constants/Scope';
import {eq} from 'Shared/utilities/equals';
import {UserProfileState} from 'Users/ducks/userProfile';
import {AgentClaims} from 'Users/models/AgentClaims';
import {AsyncUserClaims, EnrollTrackClaims, UserClaims, UserTypeClaimKey, UserTypeClaims} from 'Users/models/Claims';

export const isAgencyInStatus = (state: ReducerState, status: AgentNumberStatusId) => {
  return pipe(
    state.user.profile.selectedAgency,
    O.fold(
      () => false,
      selectedAgency => selectedAgency.overallAgentNumberStatus.id === status
    )
  );
};

export const getUserClaims = (userClaims: UserClaims) => {
  const getAgentClaims = (agentClaims: AgentClaims) =>
    pipe(
      O.fromNullable(agentClaims.impersonatedAgentClaim),
      O.fold(
        () => userClaims,
        (claims): UserClaims => ({
          ...claims,
          isImpersonating: true,
          originalUserName: userClaims.userName,
          userTypes: [
            {
              $type: UserTypeClaimKey.AgentSite,
              ...claims,
            },
          ],
        })
      )
    );

  const getEnrollTrackClaims = (userClaims: UserClaims, enrollTrackClaims: EnrollTrackClaims) =>
    pipe(
      enrollTrackClaims.impersonatedEnrollTrackClaim,
      O.fromNullable,
      O.fold(
        () => userClaims,
        (claims): UserClaims => ({
          ...claims,
          isImpersonating: true,
          originalUserName: userClaims.userName,
          userTypes: [
            ...(userClaims.isImpersonating ? userClaims.userTypes : []),
            {
              $type: UserTypeClaimKey.EnrollTrack,
              ...claims,
            },
          ],
        })
      )
    );

  const result = pipe(
    userClaims.userTypes,
    filter(userTypes => userTypes.$type === UserTypeClaimKey.AgentSite),
    head,
    O.fold(
      () => userClaims,
      agentClaims => (agentClaims.$type === UserTypeClaimKey.AgentSite ? getAgentClaims(agentClaims) : userClaims)
    )
  );

  const updatedUserClaims = pipe(
    result.userTypes,
    filter(userTypes => userTypes.$type === UserTypeClaimKey.EnrollTrack),
    head,
    O.fold(
      () => result,
      agentClaims =>
        agentClaims.$type === UserTypeClaimKey.EnrollTrack ? getEnrollTrackClaims(result, agentClaims) : result
    )
  );

  return updatedUserClaims;
};

export const selectCurrentTaxIdUserProfile = (state: UserProfileState) =>
  pipe(
    state.selectedAgency,
    O.fold(constant(''), agency => agency.maskedIdentifier)
  );
const selectAgentScopesUserProfile = (state: UserProfileState, agentClaims: AgentClaims) => {
  const currentAgency = O.fromNullable(
    agentClaims.agencyAssociations.find(eq('maskedIdentifier', selectCurrentTaxIdUserProfile(state)))
  );
  if (O.isSome(currentAgency) && currentAgency.value.overallAgentNumberStatus.id === AgentNumberStatusId.Terminated) {
    return TerminatedAgentScopes;
  }

  return pipe(
    O.fromNullable(agentClaims.agencyAssociations.find(eq('maskedIdentifier', selectCurrentTaxIdUserProfile(state)))),
    O.fold(constant([]), agency => agency.scopes)
  );
};

export const getUserScopesByClaimKey = (state: UserProfileState, userClaims: UserClaims) => (
  userTypeClaimKey: UserTypeClaimKey
) => {
  const unflattenedUserProfileInformation = getUserScopesUserProfileUnflattened(state, userClaims)!.find(
    x => x!.$type === userTypeClaimKey
  );

  return unflattenedUserProfileInformation ? unflattenedUserProfileInformation!.scopes : undefined;
};

export const loadUserClaims = (asyncClaims: Async<UserClaims>) => {
  const data = toEither(asyncClaims);
  const claims: UserClaims | undefined = pipe(
    data,
    E.map(value => new Loaded(value)),
    E.fold(
      () => undefined,
      value => value.Data
    )
  )!;

  return claims;
};

export const hasUserTypeClaimKey = (userClaims: UserClaims, userTypeClaimKey: UserTypeClaimKey) => {
  return userClaims.userTypes.some(userType => userType.$type === userTypeClaimKey);
};

export const getUserScopesUserProfileUnflattened = (state: UserProfileState, userClaims: UserClaims) => {
  return (userClaims.userTypes || []).map(userType =>
    UserTypeClaims.match(userType, {
      [UserTypeClaimKey.ApplicationEntry]: appEntryClaims => {
        return {scopes: appEntryClaims.scopes, $type: userType.$type};
      },
      [UserTypeClaimKey.EnrollTrack]: enrollTrackClaims => {
        const impersonatedEnrollTrackClaims = O.fromNullable(enrollTrackClaims.impersonatedEnrollTrackClaim);

        return O.isSome(impersonatedEnrollTrackClaims)
          ? {scopes: impersonatedEnrollTrackClaims.value.scopes, $type: userType.$type}
          : {scopes: enrollTrackClaims.scopes, $type: userType.$type};
      },
      [UserTypeClaimKey.Beneficiary]: beneficiaryClaims => {
        return {scopes: beneficiaryClaims.scopes, $type: userType.$type};
      },
      [UserTypeClaimKey.BOSTools]: bosToolsClaims => {
        return {scopes: bosToolsClaims.scopes, $type: userType.$type};
      },
      [UserTypeClaimKey.Messaging]: messagingClaims => {
        return {scopes: messagingClaims.scopes, $type: userType.$type};
      },
      [UserTypeClaimKey.MyAccessPortal]: portalClaims => {
        return {scopes: portalClaims.scopes, $type: userType.$type};
      },
      [UserTypeClaimKey.AgentSite]: agent => {
        const impersonatedAgentClaims = O.fromNullable(agent.impersonatedAgentClaim);

        return O.isSome(impersonatedAgentClaims)
          ? {
              scopes: selectAgentScopesUserProfile(state, impersonatedAgentClaims.value),
              $type: userType.$type,
            }
          : agent.agencyAssociations.length > 0
          ? {
              scopes: selectAgentScopesUserProfile(state, agent),
              $type: userType.$type,
            }
          : {
              scopes: agent.scopes,
              $type: userType.$type,
            };
      },
      Empty: undefined,
      default: () => {
        return undefined;
      },
    })
  );
};

export const getUserScopesUserProfile = (state: UserProfileState, userClaims: UserClaims) => {
  return flatten(
    (userClaims.userTypes || []).map(userType =>
      UserTypeClaims.match(userType, {
        [UserTypeClaimKey.ApplicationEntry]: appEntryClaims => appEntryClaims.scopes,
        [UserTypeClaimKey.EnrollTrack]: enrollTrackClaims => {
          const impersonatedEnrollTrackClaims = O.fromNullable(enrollTrackClaims.impersonatedEnrollTrackClaim);

          const impersonatedAgentClaims = pipe(
            userClaims.userTypes,
            findFirst(eq('$type', UserTypeClaimKey.AgentSite)),
            O.chain(agentClaims => O.fromNullable((agentClaims as AgentClaims).impersonatedAgentClaim))
          );

          return O.isSome(impersonatedAgentClaims) && O.isNone(impersonatedEnrollTrackClaims)
            ? []
            : O.isSome(impersonatedEnrollTrackClaims)
            ? impersonatedEnrollTrackClaims.value.scopes
            : enrollTrackClaims.scopes;
        },
        [UserTypeClaimKey.Beneficiary]: beneficiaryClaims => beneficiaryClaims.scopes,
        [UserTypeClaimKey.BOSTools]: bosToolsClaims => bosToolsClaims.scopes,
        [UserTypeClaimKey.Messaging]: messagingClaims => messagingClaims.scopes,
        [UserTypeClaimKey.MyAccessPortal]: portalClaims => portalClaims.scopes,
        [UserTypeClaimKey.AgentSite]: agent => {
          const impersonatedAgentClaims = O.fromNullable(agent.impersonatedAgentClaim);

          const impersonatedEnrollTrackUserClaims = pipe(
            userClaims.userTypes,
            findFirst(eq('$type', UserTypeClaimKey.EnrollTrack)),
            O.chain(enrollTrackClaims =>
              O.fromNullable((enrollTrackClaims as EnrollTrackClaims).impersonatedEnrollTrackClaim)
            )
          );

          return O.isSome(impersonatedEnrollTrackUserClaims) && O.isNone(impersonatedAgentClaims)
            ? []
            : O.isSome(impersonatedAgentClaims)
            ? selectAgentScopesUserProfile(state, impersonatedAgentClaims.value)
            : agent.agencyAssociations.length > 0
            ? selectAgentScopesUserProfile(state, agent)
            : agent.scopes;
        },
        Empty: constant([]),
        default: userType => {
          console.log(`Could not parse user type: `);
          console.log(userType);
          return [];
        },
      })
    )
  );
};

const selectAgentScopes = (state: ReducerState, agentClaims: AgentClaims) => {
  const currentAgency = O.fromNullable(
    agentClaims.agencyAssociations.find(eq('maskedIdentifier', selectCurrentTaxId(state)))
  );
  if (O.isSome(currentAgency) && currentAgency.value.overallAgentNumberStatus.id === AgentNumberStatusId.Terminated) {
    return TerminatedAgentScopes;
  }

  return pipe(
    O.fromNullable(agentClaims.agencyAssociations.find(eq('maskedIdentifier', selectCurrentTaxId(state)))),
    O.fold(constant([]), agency => agency.scopes)
  );
};

export const getUserScopes = (state: ReducerState, userClaims: UserClaims) => {
  return flatten(
    (userClaims.userTypes || []).map(userType =>
      UserTypeClaims.match(userType, {
        [UserTypeClaimKey.ApplicationEntry]: appEntryClaims => appEntryClaims.scopes,
        [UserTypeClaimKey.EnrollTrack]: enrollTrackClaims => {
          const impersonatedEnrollTrackClaims = O.fromNullable(enrollTrackClaims.impersonatedEnrollTrackClaim);

          const impersonatedAgentClaims = pipe(
            userClaims.userTypes,
            findFirst(eq('$type', UserTypeClaimKey.AgentSite)),
            O.chain(agentClaims => O.fromNullable((agentClaims as AgentClaims).impersonatedAgentClaim))
          );

          return O.isSome(impersonatedAgentClaims) && O.isNone(impersonatedEnrollTrackClaims)
            ? []
            : O.isSome(impersonatedEnrollTrackClaims)
            ? impersonatedEnrollTrackClaims.value.scopes
            : enrollTrackClaims.scopes;
        },
        [UserTypeClaimKey.MyAccessPortal]: portalClaims => portalClaims.scopes,
        [UserTypeClaimKey.Beneficiary]: beneficiaryClaims => beneficiaryClaims.scopes,
        [UserTypeClaimKey.BOSTools]: bosToolsClaims => bosToolsClaims.scopes,
        [UserTypeClaimKey.Messaging]: messagingClaims => messagingClaims.scopes,
        [UserTypeClaimKey.AgentSite]: agent => {
          const impersonatedAgentClaims = O.fromNullable(agent.impersonatedAgentClaim);

          const impersonatedEnrollTrackUserClaims = pipe(
            userClaims.userTypes,
            findFirst(eq('$type', UserTypeClaimKey.EnrollTrack)),
            O.chain(enrollTrackClaims =>
              O.fromNullable((enrollTrackClaims as EnrollTrackClaims).impersonatedEnrollTrackClaim)
            )
          );
          return O.isSome(impersonatedEnrollTrackUserClaims) && O.isNone(impersonatedAgentClaims)
            ? []
            : O.isSome(impersonatedAgentClaims)
            ? selectAgentScopes(state, impersonatedAgentClaims.value)
            : agent.agencyAssociations.length > 0
            ? selectAgentScopes(state, agent)
            : agent.scopes;
        },
        Empty: constant([]),
        default: userType => {
          console.log(`Could not parse user type: `);
          console.log(userType);
          return [];
        },
      })
    )
  );
};

export const selectUserScopesEither = (state: ReducerState) => {
  return pipe(
    E.right((claims: UserClaims) => getUserScopes(state, claims)),
    E.ap(toEither(state.user.profile.userClaims))
  );
};

export const selectUserScopes = (state: ReducerState) => {
  return pipe(
    E.right((claims: UserClaims) => getUserScopes(state, claims)),
    E.ap(toEither(state.user.profile.userClaims)),
    E.fold(constant([]), identity)
  );
};
export const selectUserClaims = (state: ReducerState): AsyncUserClaims => {
  const getCurrentUserClaims = constant(state.user.profile.userClaims);
  return getCurrentUserClaims();
};
export const selectCurrentAgentNumbers = (state: ReducerState) => state.user.profile.currentAgentNumbers;
export const selectCurrentTaxId = (state: ReducerState) =>
  pipe(
    state.user.profile.selectedAgency,
    O.fold(constant(''), agency => agency.maskedIdentifier)
  );
export const selectCurrentIdentifier = (state: ReducerState) =>
  pipe(
    state.user.profile.selectedAgency,
    O.fold(constant(''), agency => agency.encryptedIdentifier)
  );
